import { useEffect, useRef } from "react";
import { sendVideoMeetingHeartbeat } from "../utils/sendVideoMeetingHeartbeat";
import type {
  VideoRoomHeartbeatResponse,
  DeclaredVideoState,
} from "../videoMeeting.types";
import type { CallQualityMetrics } from "../utils/collectCallQualityMetrics";

const HEARTBEAT_INTERVAL_MS = 10_000; // 10 seconds

export type UseVideoMeetingHeartbeatArgs = {
  meetingId: string | null;
  userName: string | null;
  user: { id: number } | null;
  isEnabled?: boolean;
  onHeartbeat?: (response: VideoRoomHeartbeatResponse) => void;
  /** Async callback that collects WebRTC quality metrics for this tick.
   *  If provided, the result is included in the heartbeat POST body. */
  getCallQualityMetrics?: () => Promise<CallQualityMetrics | null>;
  /** Synchronous getter for the user's declared session state
   *  (mic/camera/screen-share intent + connection quality). If provided,
   *  the four fields are sent at the top level of the heartbeat body and
   *  mirrored onto meeting_{mid}_{uid} by the backend. */
  getDeclaredState?: () => DeclaredVideoState | null;
  /** Send an immediate heartbeat when the tab returns to foreground.
   *  Prevents the 30-second stale threshold from triggering transcription
   *  cleanup when Chrome throttles setInterval to 1/min in background tabs
   *  or iOS Safari suspends JS entirely. */
  resumeOnForeground?: boolean;
};

export function useVideoMeetingHeartbeat({
  meetingId,
  userName,
  user,
  isEnabled = true,
  onHeartbeat,
  getCallQualityMetrics,
  getDeclaredState,
  resumeOnForeground = false,
}: UseVideoMeetingHeartbeatArgs) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const missedHeartbeatsRef = useRef(0);
  const resumedFromBackgroundRef = useRef(false);

  useEffect(() => {
    if (!isEnabled || !meetingId || !userName) {
      return;
    }

    const sendHeartbeat = async () => {
      try {
        const metrics = await getCallQualityMetrics?.() ?? null;

        // Inject connectivity context into metrics if present
        if (metrics) {
          metrics.missed_heartbeats = missedHeartbeatsRef.current;
          metrics.resumed_from_background = resumedFromBackgroundRef.current;
        }

        const declaredState = getDeclaredState?.() ?? null;

        const response = await sendVideoMeetingHeartbeat(
          meetingId,
          userName,
          user,
          metrics,
          declaredState,
          missedHeartbeatsRef.current,
        );

        // Reset connectivity tracking after successful send
        missedHeartbeatsRef.current = 0;
        resumedFromBackgroundRef.current = false;

        onHeartbeat?.(response);
      } catch {
        missedHeartbeatsRef.current++;
      }
    };

    // Send immediately, then on interval
    sendHeartbeat();
    intervalRef.current = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL_MS);

    // On foreground return, fire an immediate heartbeat so the backend
    // doesn't consider the user stale (30s threshold).
    const handleVisibilityChange = resumeOnForeground
      ? () => {
          if (document.visibilityState === 'visible') {
            resumedFromBackgroundRef.current = true;
            sendHeartbeat();
          }
        }
      : null;

    if (handleVisibilityChange) {
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      if (handleVisibilityChange) {
        document.removeEventListener('visibilitychange', handleVisibilityChange);
      }
    };
  }, [isEnabled, meetingId, userName, user, onHeartbeat, getCallQualityMetrics, getDeclaredState, resumeOnForeground]);
}
